import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { orderService } from '../services/orderService';
import { Order } from '../services/adminService';
import { formatPrice, formatRelativeTime } from '../utils';
import Badge from '../components/UI/Badge';

const Dashboard: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadOrders = async () => {
      const response = await orderService.getMyOrders();
      if (response.success && response.data) {
        setOrders(response.data.slice(0, 5));
      }
      setLoading(false);
    };
    if (isAuthenticated) {
      loadOrders();
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role === 'admin') {
    return <Navigate to="/admin" replace />;
  }

  const stats = {
    totalOrders: orders.length,
    activeOrders: orders.filter(o => ['pending', 'confirmed', 'processing', 'shipped'].includes(o.status)).length,
    completedOrders: orders.filter(o => o.status === 'delivered').length,
    totalSpent: orders.reduce((sum, o) => sum + o.totalAmount, 0),
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-r from-green-600 to-green-800 rounded-2xl p-8 text-white mb-8">
          <h1 className="text-3xl font-bold mb-2">Welcome back, {user?.name}! 🌾</h1>
          <p className="text-green-100">Here's your {user?.role} dashboard</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Orders</p>
                <p className="text-3xl font-bold text-gray-900">{stats.totalOrders}</p>
              </div>
              <div className="text-4xl">📦</div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Active Orders</p>
                <p className="text-3xl font-bold text-blue-600">{stats.activeOrders}</p>
              </div>
              <div className="text-4xl">🚀</div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Completed</p>
                <p className="text-3xl font-bold text-green-600">{stats.completedOrders}</p>
              </div>
              <div className="text-4xl">✅</div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Spent</p>
                <p className="text-2xl font-bold text-gray-900">{formatPrice(stats.totalSpent)}</p>
              </div>
              <div className="text-4xl">💰</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Recent Orders</h2>
              <Link to="/orders" className="text-green-600 hover:text-green-700 font-medium text-sm">
                View All →
              </Link>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-12">
                <span className="text-6xl block mb-4">📦</span>
                <p className="text-gray-600 mb-4">No orders yet</p>
                <Link to="/products" className="text-green-600 hover:text-green-700 font-medium">
                  Start Shopping →
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {orders.map(order => (
                  <Link
                    key={order._id}
                    to={`/orders/${order._id}`}
                    className="block border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-gray-900">Order #{order._id.slice(-8)}</p>
                        <p className="text-sm text-gray-600">{formatRelativeTime(order.createdAt)}</p>
                      </div>
                      <Badge variant={order.status === 'delivered' ? 'success' : 'info'}>{order.status}</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-600">{order.products.length} item(s)</p>
                      <p className="font-bold text-green-600">{formatPrice(order.totalAmount)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-bold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <Link to="/products" className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-all">
                  <span className="text-2xl">🛒</span>
                  <span className="font-medium text-green-900">Browse Products</span>
                </Link>
                <Link to="/cart" className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-all">
                  <span className="text-2xl">🛍️</span>
                  <span className="font-medium text-blue-900">View Cart</span>
                </Link>
                <Link to="/farmers" className="flex items-center space-x-3 p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-all">
                  <span className="text-2xl">🌾</span>
                  <span className="font-medium text-purple-900">Find Farmers</span>
                </Link>
                <Link to="/profile" className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all">
                  <span className="text-2xl">👤</span>
                  <span className="font-medium text-gray-900">Edit Profile</span>
                </Link>
                {user?.role === 'farmer' && (
                  <Link to="/farmer/products/new" className="flex items-center space-x-3 p-3 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-all">
                    <span className="text-2xl">➕</span>
                    <span className="font-medium text-yellow-900">Add Product</span>
                  </Link>
                )}
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-600 to-green-800 rounded-xl p-6 text-white">
              <h3 className="font-bold mb-2">🎁 Special Offer!</h3>
              <p className="text-sm text-green-100 mb-4">Use code WELCOME10 for 10% off your next order</p>
              <Link to="/products" className="block w-full py-2 bg-white text-green-600 text-center rounded-lg font-medium hover:bg-green-50 transition-all">
                Shop Now
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

